import * as React from "react";
import { useLingui } from "@lingui/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EnhancedSelectOption {
  value: string;
  label: string;
  emoji?: string;
  suggested?: boolean;
  color?: string;
  hideCheckbox?: boolean;
}

export interface EnhancedMultiSelectProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  options?: EnhancedSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  /** Required state reported by the surrounding form (`FormLabel required`). */
  "aria-required"?: boolean | "true" | "false";
  maxHeight?: number;
  renderOption?: (option: EnhancedSelectOption) => React.ReactNode;
  /** When provided, delegates search to the parent instead of filtering client-side */
  onSearchChange?: (term: string) => void;
  /** Shows a loading indicator while server search is in progress */
  isLoading?: boolean;
  /**
   * Notified when the dropdown opens/closes. Parents that gate a server query
   * on popover-open (e.g. only fetching the children list while the dropdown
   * is open) read this.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional footer note shown below the option list — e.g.
   * "showing 20 of 189 — type to refine" for server-paged result sets.
   */
  footerNote?: React.ReactNode;
}

const EnhancedMultiSelect = React.forwardRef((props: EnhancedMultiSelectProps, ref: React.Ref<HTMLButtonElement>) => {
  const { i18n } = useLingui();
  const {
    value = [],
    onChange,
    options = [],
    placeholder,
    disabled,
    className,
    id,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    maxHeight = 300,
    renderOption,
    onSearchChange,
    isLoading,
    onOpenChange,
    footerNote,
  } = props;
    // Ensure value is always an array to prevent runtime errors
    const safeValue = React.useMemo(() => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return [];
    }, [value]);
    const [open, setOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");

    // Notify the parent on open/close so queries can be gated on popover-open.
    const handleOpenChange = React.useCallback(
      (next: boolean) => {
        setOpen(next);
        onOpenChange?.(next);
      },
      [onOpenChange]
    );

    // Generate unique IDs for accessibility
    const popoverContentId = React.useId();

    // Separate suggested and regular options
    const suggestedOptions = React.useMemo(
      () => options.filter((opt) => opt.suggested),
      [options]
    );

    const regularOptions = React.useMemo(
      () =>
        options
          .filter((opt) => !opt.suggested),
      [options]
    );

    // Filter options based on search term
    const filteredSuggested = React.useMemo(
      () =>
        suggestedOptions.filter(
          (opt) =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      [suggestedOptions, searchTerm]
    );

    const filteredRegular = React.useMemo(
      () =>
        // When server-side search is active, parent controls options — skip client filtering
        onSearchChange
          ? regularOptions
          : regularOptions.filter(
              (opt) =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                opt.value.toLowerCase().includes(searchTerm.toLowerCase())
            ),
      [regularOptions, searchTerm, onSearchChange]
    );

    const handleSelect = (optionValue: string, checked: boolean) => {
      if (checked) {
        // Guard against duplicate selections
        onChange?.(
          safeValue.includes(optionValue) ? safeValue : [...safeValue, optionValue]
        );
      } else {
        onChange?.(safeValue.filter((v) => v !== optionValue));
      }
    };

    const selectedCount = safeValue.length;
    const selectedLabels = React.useMemo(() => {
      return options
        .filter((opt) => safeValue.includes(opt.value))
        .map((opt) => `${opt.emoji || ""} ${opt.label}`.trim())
        .join(", ");
    }, [safeValue, options]);

    const renderOptionItem = (option: EnhancedSelectOption) => (
      <div
        key={option.value}
        className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer w-full"
        onClick={() => {
          const isCurrentlySelected = safeValue.includes(option.value);
          handleSelect(option.value, !isCurrentlySelected);
        }}
      >
        {!option.hideCheckbox && (
          <Checkbox
            checked={safeValue.includes(option.value)}
            onCheckedChange={(checked) => handleSelect(option.value, !!checked)}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "flex-shrink-0",
              !safeValue.includes(option.value) &&
                "border-muted-foreground/50 data-[state=unchecked]:border-muted-foreground/50"
            )}
          />
        )}
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          {renderOption ? (
            renderOption(option)
          ) : option.color ? (
            <Badge
              variant="secondary"
              style={{
                backgroundColor: option.color ? `${option.color}20` : undefined,
                borderColor: option.color || undefined,
                color: option.color || undefined,
              }}
              className="cursor-pointer truncate"
            >
              {option.label}
            </Badge>
          ) : (
            <>
              {option.emoji && (
                <span key="emoji" className="text-lg flex-shrink-0">{option.emoji}</span>
              )}
              <Label key="label" className="cursor-pointer flex-1 truncate">
                {option.label}
              </Label>
            </>
          )}
        </div>
      </div>
    );

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-required={ariaRequired}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverContentId}
            aria-haspopup="listbox"
            className={cn(
              "w-full justify-between text-left font-normal",
              !selectedCount && "text-muted-foreground",
              className
            )}
            disabled={disabled}
          >
            <span className="truncate">
              {selectedCount > 0
                ? selectedCount === 1
                  ? selectedLabels
                  : i18n._({
                      id: "common.multiselect.selectedCount",
                      message: "{count} selected",
                      values: { count: selectedCount },
                    })
                : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          id={popoverContentId}
          role="listbox"
          className="w-[--radix-popover-trigger-width] p-0 min-w-[200px] max-w-[400px]"
          align="start"
          sideOffset={5}
          avoidCollisions={true}
          collisionPadding={10}
        >
          <div className="sticky top-0 z-10 bg-background p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder={i18n._({
                  id: "common.multiselect.searchPlaceholder",
                  message: "Search...",
                })}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  onSearchChange?.(e.target.value);
                }}
                className="pl-8"
              />
            </div>
          </div>

          <div 
            className="overflow-y-auto overflow-x-hidden" 
            style={{ height: `min(60vh, ${maxHeight}px)` }}
          >
            <div className="w-full">
              {/* Suggested options */}
              {filteredSuggested.length > 0 && (
                <div>
                  {filteredSuggested.map(renderOptionItem)}
                  {filteredRegular.length > 0 && (
                    <div className="px-3 py-2">
                      <Separator />
                    </div>
                  )}
                </div>
              )}

              {/* Regular options */}
              {filteredRegular.length > 0 && (
                <div>{filteredRegular.map(renderOptionItem)}</div>
              )}

              {/* Loading indicator for server-side search */}
              {isLoading && (
                <div className="px-3 py-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>
                    {i18n._({
                      id: "common.multiselect.searching",
                      message: "Searching...",
                    })}
                  </span>
                </div>
              )}

              {/* No results */}
              {!isLoading && filteredSuggested.length === 0 && filteredRegular.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {i18n._({
                    id: "common.multiselect.noResults",
                    message: "No results found",
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Optional footer note (e.g. "showing 20 of N — type to refine") */}
          {footerNote && (
            <div className="sticky bottom-0 z-10 border-t bg-background px-3 py-2 text-center text-xs text-muted-foreground">
              {footerNote}
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
});

EnhancedMultiSelect.displayName = "EnhancedMultiSelect";

export { EnhancedMultiSelect };
